import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Appointment } from "../../../state/appointments";
import {
  APPOINTMENT_STATUS,
  AppointmentStatus
} from "../../../hooks/useAppointments";
import { typography } from "../../../utils/typography";
import { semanticColors } from "../../../utils/colors";
import { outlinedFieldSx, searchIconSx } from "./formStyles";
import AppointmentHistoryTable from "./AppointmentHistoryTable";

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  date: moment.Moment | null;
  onDateChange: (value: moment.Moment | null) => void;
  /** Empty string means "all statuses". */
  status: AppointmentStatus | "";
  onStatusChange: (value: AppointmentStatus | "") => void;
  /** Past appointments (already filtered/sorted by the caller). */
  rows: Appointment[];
  pictureMap: Record<string, string>;
  /** patientName (lowercased) → patient number. */
  nhsIdMap: Record<string, string>;
}

// The statuses a past consultation can have — a past appointment is never
// "Scheduled", so offering it would only ever return an empty table.
const STATUS_OPTIONS = [
  { value: APPOINTMENT_STATUS.Done, labelKey: "videoConsultations.statusDone" },
  {
    value: APPOINTMENT_STATUS.Lapsed,
    labelKey: "videoConsultations.statusLapsed"
  },
  {
    value: APPOINTMENT_STATUS.Cancelled,
    labelKey: "videoConsultations.statusCancelled"
  }
] as const;

const filtersRowSx = {
  display: "flex",
  gap: 2,
  mb: 3,
  alignItems: "center"
};

const searchFieldSx = {
  ...outlinedFieldSx,
  flex: "none",
  width: { xs: "calc(100% - 236px)", sm: "38%" }
};

const statusFieldSx = { ...outlinedFieldSx, width: "200px", flexShrink: 0 };

const dateBoxSx = { ml: "auto", position: "relative", flexShrink: 0 };

const datePickerFieldSx = { ...outlinedFieldSx, width: "220px" };

const clearDateBtnSx = {
  position: "absolute",
  right: 36,
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 2,
  p: "3px",
  color: semanticColors.text.secondary,
  "&:hover": {
    color: semanticColors.text.primary,
    bgcolor: "rgba(0,0,0,0.04)"
  }
};

const noResultsSx = {
  ...typography.body.l,
  color: semanticColors.text.secondary,
  mt: 4,
  textAlign: "center"
};

export default function HistoryTab({
  search,
  onSearchChange,
  date,
  onDateChange,
  status,
  onStatusChange,
  rows,
  pictureMap,
  nhsIdMap
}: Props) {
  const { t } = useTranslation();

  return (
    <Box>
      {/* Filters */}
      <Box sx={filtersRowSx}>
        <TextField
          placeholder={t("videoConsultations.searchByPatient")}
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={searchIconSx} />
              </InputAdornment>
            )
          }}
          sx={searchFieldSx}
        />
        <Box sx={dateBoxSx}>
          <DateTimePicker
            label={t("videoConsultations.filterByDate")}
            value={date}
            onChange={val => onDateChange(val)}
            views={["year", "month", "day"]}
            slotProps={{ textField: { sx: datePickerFieldSx } }}
          />
          {date && (
            <IconButton
              size="small"
              onClick={() => onDateChange(null)}
              sx={clearDateBtnSx}
            >
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
        <TextField
          select
          label={t("videoConsultations.filterByStatus")}
          value={status}
          onChange={e =>
            onStatusChange(e.target.value as AppointmentStatus | "")
          }
          sx={statusFieldSx}
        >
          <MenuItem value="">{t("videoConsultations.allStatuses")}</MenuItem>
          {STATUS_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Divider sx={{ mb: 0 }} />

      {rows.length === 0 ? (
        <Typography sx={noResultsSx}>
          {t("videoConsultations.noResults")}
        </Typography>
      ) : (
        <AppointmentHistoryTable
          rows={rows}
          pictureMap={pictureMap}
          nhsIdMap={nhsIdMap}
        />
      )}
    </Box>
  );
}
